import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, CheckCircle, AlertTriangle, Users, AlarmClock, ArrowRight, Mail, Loader2 } from 'lucide-react';

export default function FollowupDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersList, setUsersList] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignFollowupId, setAssignFollowupId] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, usersRes] = await Promise.all([
          api.get('/followups/dashboard'),
          api.get('/users').catch(() => ({ data: [] }))
        ]);
        setDashboard(dashRes.data);
        setUsersList(usersRes.data);

        // Auto-send reminders for due/overdue followups on page load
        try {
          await api.post('/followups/send-reminders');
        } catch {}
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const refresh = async () => {
    try {
      const res = await api.get('/followups/dashboard');
      setDashboard(res.data);
    } catch (err) { toast.error('Failed to refresh'); }
  };

  const completeFollowup = async (fid) => {
    try {
      await api.put('/followups/' + fid, { status: 'completed', completed_at: new Date().toISOString() });
      toast.success('Marked complete');
      refresh();
    } catch (err) { toast.error('Failed to complete followup'); }
  };

  const snoozeFollowup = async (fid, mins) => {
    try {
      await api.put('/followups/' + fid + '/snooze?minutes=' + mins);
      toast.success('Snoozed');
      refresh();
    } catch (err) { toast.error('Failed to snooze'); }
  };

  const assignFollowup = async () => {
    if (!assignUserId) { toast.error('Select a user'); return; }
    try {
      await api.put('/followups/' + assignFollowupId + '/assign?assigned_to=' + assignUserId);
      toast.success('Assigned');
      setShowAssign(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const canAssign = ['super_admin', 'company_admin', 'manager'].includes(user?.role);

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await api.post('/followups/send-reminders');
      const { sent, already_sent, total_due } = res.data;
      if (sent > 0) {
        toast.success(`Sent ${sent} email reminder${sent > 1 ? 's' : ''}`);
      } else if (total_due > 0 && sent === 0) {
        toast.info(`${total_due} due follow-up${total_due > 1 ? 's' : ''} found. Email delivery requires a verified domain on Resend.`);
      } else if (already_sent > 0) {
        toast.info('Reminders already processed for all due follow-ups');
      } else {
        toast.info('No follow-ups due in the next 30 minutes');
      }
    } catch {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard || { total: 0, pending: 0, completed: 0, overdue: 0, pending_list: [], overdue_list: [], completed_list: [] };

  const renderRow = (f) => {
    const scheduled = f.scheduled_at ? new Date(f.scheduled_at).toLocaleString() : '-';
    return (
      <TableRow key={f.id}>
        <TableCell>
          <Badge variant="outline" className="capitalize text-xs">{f.type || 'call'}</Badge>
        </TableCell>
        <TableCell>
          <div className="text-xs">
            <span className="font-medium text-foreground">{f.client_name || 'Unknown'}</span>
            {f.destination && <span className="text-muted-foreground ml-1">- {f.destination}</span>}
          </div>
          {f.inquiry_id && <span className="text-[10px] text-muted-foreground font-mono">{f.inquiry_id}</span>}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{scheduled}</TableCell>
        <TableCell className="text-xs text-muted-foreground max-w-48 truncate">{f.notes || '-'}</TableCell>
        <TableCell className="text-xs">{f.assigned_to_name || '-'}</TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1">
            {f.lead_id && (
              <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-0.5"
                onClick={() => navigate('/dashboard/leads/' + f.lead_id)}>
                <ArrowRight className="w-3 h-3" /> Lead
              </Button>
            )}
            <Select onValueChange={(v) => snoozeFollowup(f.id, parseInt(v))}>
              <SelectTrigger className="h-6 text-[10px] w-16">
                <AlarmClock className="w-3 h-3" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 min</SelectItem>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="1440">Tomorrow</SelectItem>
              </SelectContent>
            </Select>
            {canAssign && (
              <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-0.5"
                onClick={() => { setAssignFollowupId(f.id); setShowAssign(true); }}
                data-testid={'assign-btn-' + f.id}>
                <Users className="w-3 h-3" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-600 gap-0.5"
              onClick={() => completeFollowup(f.id)}
              data-testid={'complete-btn-' + f.id}>
              <CheckCircle className="w-3 h-3" /> Done
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead className="text-xs">Type</TableHead>
        <TableHead className="text-xs">Client</TableHead>
        <TableHead className="text-xs">Scheduled</TableHead>
        <TableHead className="text-xs">Notes</TableHead>
        <TableHead className="text-xs">Assigned</TableHead>
        <TableHead className="text-xs text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="space-y-5 animate-fade-in" data-testid="followup-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">Follow-up Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track and manage all your follow-ups</p>
        </div>
        <Button
          onClick={sendReminders}
          disabled={sendingReminders}
          variant="outline"
          className="gap-2 border-voyage-orange/40 text-voyage-orange hover:bg-voyage-orange/10 hover:border-voyage-orange"
          data-testid="send-reminders-btn"
        >
          {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Send Email Reminders
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border border-border card-hover-lift stat-card-blue">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><Clock className="w-4 h-4 text-blue-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Total</p><p className="font-heading font-bold text-xl">{d.total}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-border card-hover-lift stat-card-amber">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center"><Clock className="w-4 h-4 text-amber-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Pending</p><p className="font-heading font-bold text-xl text-amber-600">{d.pending}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-border card-hover-lift stat-card-rose">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Overdue</p><p className="font-heading font-bold text-xl text-red-600">{d.overdue}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-border card-hover-lift stat-card-emerald">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-emerald-500" /></div>
            <div><p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Completed</p><p className="font-heading font-bold text-xl text-emerald-600">{d.completed}</p></div>
          </CardContent>
        </Card>
      </div>

      {d.overdue_list.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/5" data-testid="overdue-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-destructive flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Overdue Follow-ups ({d.overdue_list.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                {tableHeader}
                <TableBody>{d.overdue_list.map(renderRow)}</TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-border" data-testid="pending-section">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" /> Pending Follow-ups ({d.pending_list.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {d.pending_list.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                {tableHeader}
                <TableBody>{d.pending_list.map(renderRow)}</TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">No pending follow-ups</p>
          )}
        </CardContent>
      </Card>

      {d.completed_list.length > 0 && (
        <Card className="border border-border" data-testid="completed-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2 text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-emerald-500" /> Recently Completed ({d.completed_list.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {d.completed_list.map(f => (
                <div key={f.id} className="flex items-center justify-between p-2 rounded-lg border border-border/30 bg-muted/20 text-sm">
                  <div>
                    <span className="text-muted-foreground capitalize">{f.type}</span>
                    {f.notes && <span className="text-xs text-muted-foreground ml-2">- {f.notes}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {f.completed_at ? new Date(f.completed_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent data-testid="assign-followup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Follow-up</DialogTitle>
            <DialogDescription>Select a team member to assign this follow-up to.</DialogDescription>
          </DialogHeader>
          <Select value={assignUserId} onValueChange={setAssignUserId}>
            <SelectTrigger data-testid="assign-user-select">
              <SelectValue placeholder="Select team member" />
            </SelectTrigger>
            <SelectContent>
              {usersList.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={assignFollowup} className="bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="confirm-assign-btn">
            Assign
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
